import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import orderBy from 'lodash/orderBy';
import IdentityContext from '../../../contexts/IdentityContext';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { getUserMaterialWithScores, UserMaterial } from '../_api/UserMaterialApi';
import ModuleContext from './ModuleContext';
import TableOfContentsContext from './TableOfContentsContext';

type UserMaterialContextValue = {
  userMaterial: UserMaterial[];
  setUserMaterial: React.Dispatch<React.SetStateAction<UserMaterial[]>>;
  fetchUserMaterial: () => Promise<void>;
};

const UserMaterialContext = createContext<UserMaterialContextValue>({
  userMaterial: [],
  setUserMaterial: () => undefined,
  fetchUserMaterial: () => Promise.resolve(),
});

export default UserMaterialContext;

export function UserMaterialContextProvider({ children }: PropsWithChildren) {
  const handleError = useThrowAsyncError();

  const { identity } = useContext(IdentityContext);
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);

  const [userMaterial, setUserMaterial] = useState<UserMaterial[]>([]);

  const fetchUserMaterial = useCallback(async () => {
    if (!selectedModule || !selectedNode) return;

    try {
      const { data } = await getUserMaterialWithScores(selectedModule.id, selectedNode.id);

      setUserMaterial(orderBy(data, ['lastUpdatedAt'], ['desc']));
    } catch (e) {
      handleError(e);
    }
  }, [handleError, selectedModule, selectedNode]);

  useEffect(() => {
    if (identity.isTeacherAlike) fetchUserMaterial();
  }, [fetchUserMaterial, identity.isTeacherAlike]);

  return (
    <UserMaterialContext.Provider
      value={{
        userMaterial,
        setUserMaterial,
        fetchUserMaterial,
      }}
    >
      {children}
    </UserMaterialContext.Provider>
  );
}
